import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, Logout as LogoutIcon } from '@mui/icons-material';
import { TaskService, AuthService } from '../services/api';
import { Task, TaskStatus } from '../types/task';
import TaskCard from '../components/TaskCard';
import EditTaskDialog from '../components/EditTaskDialog';
import { toast } from 'react-toastify';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentTask, setCurrentTask] = useState<Task | null>(null);

  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    try {
      setLoading(true);
      const tasks = await TaskService.getTasks();
      setTasks(tasks || []);
    } catch (error) {
      console.error('Failed to load tasks:', error);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEditTask = (task: Task) => {
    setCurrentTask(task);
    setEditDialogOpen(true);
  };

  const handleAddTask = () => {
    setCurrentTask(null);
    setEditDialogOpen(true);
  };

  const handleUpdateTask = async (task: Task) => {
    try {
      await TaskService.updateTask(task.id, task);
      setEditDialogOpen(false);
      loadTasks();
    } catch (error) {
      console.error('Failed to update task:', error);
    }
  };

  const handleDeleteTask = async (taskId: number) => {
    try {
      await TaskService.deleteTask(taskId);
      loadTasks();
    } catch (error) {
      console.error('Failed to delete task:', error);
    }
  };

  const handleDragStart = (e: React.DragEvent, task: Task) => {
    e.dataTransfer.setData('taskId', task.id.toString());
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (e: React.DragEvent, status: TaskStatus) => {
    e.preventDefault();
    const taskId = parseInt(e.dataTransfer.getData('taskId'));
    const task = tasks.find((t) => t.id === taskId);
    if (task && task.status !== status) {
      await handleUpdateTask({ ...task, status });
    }
  };

  const getTasksByStatus = (status: TaskStatus) => {
    // Get tasks by status and sort by priority (1 is highest)
    return tasks
      .filter((task) => task.status === status)
      .sort((a, b) => a.priority - b.priority);
  };

  const priorityMarks = [
    { value: 1, label: 'Highest' },
    { value: 5, label: 'Medium' },
    { value: 10, label: 'Lowest' },
  ];

  // Define the order we want to display the status sections
  const statusDisplayOrder = [
    TaskStatus.IN_PROGRESS,
    TaskStatus.TODO,
    TaskStatus.DONE
  ];

  // Helper function to get section-specific styles
  const getSectionStyle = (status: TaskStatus) => {
    const baseStyle = {
      p: 2,
      backgroundColor: '#f5f5f5',
      width: '100%',
    };

    // Make IN_PROGRESS section deeper
    if (status === TaskStatus.IN_PROGRESS) {
      return {
        ...baseStyle,
        minHeight: '300px', // Deeper height for IN_PROGRESS
      };
    }

    return {
      ...baseStyle,
      minHeight: '200px',
    };
  };

  const handleLogout = () => {
    AuthService.logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Executive Decision
        </Typography>
        <Box>
          <Tooltip title="Logout">
            <IconButton onClick={handleLogout} color="primary">
              <LogoutIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddTask}
          >
            Add Task
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3} direction="column">
        {statusDisplayOrder.map((status) => (
          <Grid item xs={12} key={status}>
            <Paper
              sx={getSectionStyle(status)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, status)}
            >
              <Typography variant="h6" gutterBottom>
                {status}
              </Typography>
              <Grid container spacing={2}>
                {getTasksByStatus(status).map((task) => (
                  <Grid item xs={12} sm={6} md={2.4} key={task.id}>
                    <div
                      draggable
                      onDragStart={(e) => handleDragStart(e, task)}
                    >
                      <TaskCard
                        task={task}
                        onEdit={handleEditTask}
                        onDelete={handleDeleteTask}
                      />
                    </div>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Edit/Create Task Dialog */}
      <EditTaskDialog
        open={editDialogOpen}
        task={currentTask}
        onClose={() => setEditDialogOpen(false)}
        onSave={handleUpdateTask}
        onTaskCreated={loadTasks}
      />
    </Container>
  );
};

export default Dashboard; 